import * as Api from '@ViewModels';
import * as chrono from 'chrono-node';

export interface IQuickAddParseResult {
	title: string;
	dueDate: Date | null;
	hasDueTime: boolean;
	dateText: string | null;
	priority: Api.ActionItemPriority | null;
}

export type MentionKind = 'contact' | 'user';

export interface IActiveMention {
	kind: MentionKind;
	query: string;
	start: number;
	end: number;
}

const PRIORITY_TOKENS: Record<string, Api.ActionItemPriority> = {
	'!high': 'High',
	'!h': 'High',
	'!medium': 'Medium',
	'!med': 'Medium',
	'!m': 'Medium',
	'!low': 'Low',
	'!l': 'Low',
	p1: 'High',
	p2: 'Medium',
	p3: 'Low',
};

const PRIORITY_PATTERN = /(^|\s)(!high|!h|!medium|!med|!m|!low|!l|p1|p2|p3)(?=\s|$)/i;
const ASSIGN_PREFIX_PATTERN = /(?:^|\s)(?:for|assign(?:ed)?(?:\s+to)?)\s+$/i;
const DATE_CONNECTOR_PATTERN = /\s+(?:on|at|by|due|for)\s*$/i;

function collapseWhitespace(text: string): string {
	return text.replace(/\s+/g, ' ').trim();
}

export function extractPriority(text: string): { text: string; priority: Api.ActionItemPriority | null } {
	const match = PRIORITY_PATTERN.exec(text);
	if (!match) {
		return { text, priority: null };
	}
	const priority = PRIORITY_TOKENS[match[2].toLowerCase()] ?? null;
	const stripped = text.slice(0, match.index) + text.slice(match.index + match[0].length);
	return { text: collapseWhitespace(stripped), priority };
}

export function extractDate(
	text: string,
	now: Date
): { text: string; dueDate: Date | null; hasDueTime: boolean; dateText: string | null } {
	const results = chrono.casual.parse(text, now, { forwardDate: true });
	const result = results[0];
	if (!result) {
		return { text, dueDate: null, hasDueTime: false, dateText: null };
	}
	const hasDueTime = result.start.isCertain('hour');
	const dueDate = result.start.date();
	const before = text.slice(0, result.index).replace(DATE_CONNECTOR_PATTERN, '');
	const after = text.slice(result.index + result.text.length);
	return {
		text: collapseWhitespace(`${before} ${after}`),
		dueDate: hasDueTime ? dueDate : new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()),
		hasDueTime,
		dateText: result.text,
	};
}

/** Parse a quick-add line into a title plus structured fields. Mentions are resolved interactively, not here. */
export function parseQuickAdd(text: string, now = new Date()): IQuickAddParseResult {
	const withoutPriority = extractPriority(text);
	const withoutDate = extractDate(withoutPriority.text, now);
	return {
		title: collapseWhitespace(withoutDate.text).replace(/[,\s]+$/, ''),
		dueDate: withoutDate.dueDate,
		hasDueTime: withoutDate.hasDueTime,
		dateText: withoutDate.dateText,
		priority: withoutPriority.priority,
	};
}

/** The `@mention` token the caret is inside of, if any, and whether it names a contact or a teammate. */
export function getActiveMention(text: string, caret: number): IActiveMention | null {
	const upToCaret = text.slice(0, caret);
	const atIndex = upToCaret.lastIndexOf('@');
	if (atIndex < 0) {
		return null;
	}
	if (atIndex > 0 && !/\s/.test(upToCaret[atIndex - 1])) {
		return null;
	}
	const query = upToCaret.slice(atIndex + 1);
	if (/\s{2,}|\n/.test(query)) {
		return null;
	}
	const prefix = upToCaret.slice(0, atIndex);
	const kind: MentionKind = ASSIGN_PREFIX_PATTERN.test(prefix) ? 'user' : 'contact';
	return { kind, query, start: atIndex, end: caret };
}

/** Remove a resolved mention (and the assignment word before a teammate mention) from the text. */
export function removeMention(text: string, mention: IActiveMention): string {
	let start = mention.start;
	if (mention.kind === 'user') {
		const prefix = text.slice(0, start);
		const match = ASSIGN_PREFIX_PATTERN.exec(prefix);
		if (match) {
			start = match.index;
		}
	}
	return collapseWhitespace(text.slice(0, start) + ' ' + text.slice(mention.end));
}

/** Swap a resolved contact or company mention for its display name so the title still reads naturally. */
export function replaceMention(text: string, mention: IActiveMention, name: string): string {
	return collapseWhitespace(`${text.slice(0, mention.start)}${name} ${text.slice(mention.end)}`);
}

/** Character ranges of every known name inside the text, for highlighting; longest names win overlaps. */
export function findNameRanges(text: string, names: string[]): { start: number; end: number }[] {
	const ranges: { start: number; end: number }[] = [];
	const lower = text.toLowerCase();
	for (const name of [...names].filter(Boolean).sort((a, b) => b.length - a.length)) {
		const needle = name.toLowerCase();
		let from = 0;
		while (from <= lower.length) {
			const at = lower.indexOf(needle, from);
			if (at < 0) {
				break;
			}
			const end = at + needle.length;
			if (!ranges.some(range => at < range.end && end > range.start)) {
				ranges.push({ start: at, end });
			}
			from = end;
		}
	}
	return ranges.sort((a, b) => a.start - b.start);
}
